import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import {
    Alert,
    Button,
    Grid,
    Paper,
    TextField,
    Theme,
    Typography,
    Link,
    FormControl,
    Input,
    InputAdornment,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { ContentCopy } from '@mui/icons-material';

import { RootState } from '../../ReduxStore';

import ssologinImage from '../../Images/ssologin.png';
import ZkiliRedirectsApi from '../../API/ZkiliRedirectsApi';
import useSnackbar from '../../Hooks/useSnackbar';
import useStoredState from '../../Hooks/useStoredState';
import copyToClipboard from '../../Functions/CopyTextToClipBoard';
import { fetchShips, fetchSystems } from '../../Slices/sdeSlice';
import SEO from '../../Components/SEO';

const useStyles = makeStyles((theme: Theme) => ({
    description: {
        padding: theme.spacing(2)
    },
    copyPaper: {
        padding: theme.spacing(1)
    },
    previewPaper: {
        display: 'flex',
        padding: theme.spacing(1),
        maxWidth: 516,
        backgroundColor: `#2f3136`,
        borderLeft: `4px solid rgb(32, 34, 37)`,
        alignSelf: 'flex-start'
    },
    previewContainer: {
        display: 'flex',
        justifyContent: 'center'
    },
    previewDomain: {
        color: 'rgb(220, 221, 222)'
    },
    previewLink: {
        ...theme.typography.subtitle1,
        fontSize: 16
    },
    previewText: {
        ...theme.typography.body1,
        fontSize: 14,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    previewImgLink: {
        width: 80,
        height: 80
    },
    previewImg: {
        width: 80,
        height: 80,
        borderRadius: 4
    },
    formPaper: {
        padding: theme.spacing(1)
    },
    formField: {
        width: '100%'
    },
    loginFirst: {
        textAlign: 'center'
    },
    loginImageLink: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        maxHeight: 42
    },
    cancelButton: {
        marginRight: theme.spacing(1)
    }
}));

const isValidRedirectLink = (link): boolean => {
    let url;
    try {
        url = new URL(link);
    } catch (_) {
        return false;
    }
    if (url.protocol !== 'https:') return false;
    if (url.host !== 'zkillboard.com') return false;

    return true;
};
const systemFilterOptions = createFilterOptions({
    matchFrom: 'any',
    limit: 500
});

const ZkillCustom = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const { openSnackbar } = useSnackbar();
    const { id } = useParams();
    const user = useSelector((state: RootState) => state.user.loggedIn);
    const ships = useSelector((state: RootState) => state.sde.ships);
    const systems = useSelector((state: RootState) => state.sde.systems);
    const [config, setConfig] = useState({
        redirect: 'https://zkillboard.com/kill/29682967/',
        name: 'A Ragnarok',
        corporation: 'Capital Fusion.',
        ship: 'Ragnarok',
        shipId: 23773,
        aggressorName: 'Rya Anthar',
        aggressorCorporation: 'Goonswarm Federation',
        aggressorShip: 'Revenant',
        system: 'Jita',
        region: 'Vale of the Silent',
        value: 95727929280.66
    });
    const [shownInfo, setShownInfo] = useStoredState('shownInfo1', false);
    const [shownWarning, setShownWarning] = useStoredState('setShownWarning1', false);
    const [linkId, setLinkId] = useState(null);
    const [modified, setModified] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [shipOptions, setShipOptions] = useState([]);
    const [systemOptions, setSystemOptions] = useState([]);
    const isNewLink = !id || id === 'create';

    const redirectError = config.redirect && isValidRedirectLink(config.redirect) === false;
    const nameError = !config.name || config.name.length > 37 || config.name.length < 1;
    const corporationError = !config.corporation || config.corporation.length > 37 || config.corporation.length < 1;
    const shipError = !config.ship || config.ship.length > 43 || config.ship.length < 1;
    const shipIdError = !config.shipId;
    const aggressorNameError =
        !config.aggressorName || config.aggressorName.length > 37 || config.aggressorName.length < 1;
    const aggressorCorporationError =
        !config.aggressorCorporation ||
        config.aggressorCorporation.length > 37 ||
        config.aggressorCorporation.length < 1;
    const aggressorShipError =
        !config.aggressorShip || config.aggressorShip.length > 30 || config.aggressorShip.length < 1;
    const systemError = !config.system || config.system.length > 30 || config.system.length < 1;
    const regionError = !config.region || config.region.length > 30 || config.region.length < 1;
    const valueError = !config.value || config.value < 0 || config.value > 10000000000000;

    const formIsValid =
        !redirectError &&
        !nameError &&
        !corporationError &&
        !shipError &&
        !shipIdError &&
        !aggressorShipError &&
        !aggressorNameError &&
        !aggressorCorporationError &&
        !systemError &&
        !regionError &&
        !valueError;

    const onChange = key => e => {
        setModified(true);
        let configCopy = { ...config };
        configCopy[key] = e.target.value;
        setConfig(configCopy);
    };
    const onShipChange = (e, newValue) => {
        setModified(true);
        if (!newValue) return;

        let configCopy = { ...config };
        configCopy.ship = newValue;

        // get the ship ID for the ship name
        configCopy.shipId = ships.find(system => system.typeName === newValue).typeID;
        setConfig(configCopy);
    };
    const onAggressorShipChange = (e, newValue) => {
        setModified(true);
        if (!newValue) return;

        let configCopy = { ...config };
        configCopy.aggressorShip = newValue;
        setConfig(configCopy);
    };
    const onSystemChange = (e, newValue) => {
        setModified(true);
        if (!newValue) return;

        let configCopy = { ...config };
        configCopy.system = newValue;

        // Get the associated system name
        const systemInfo = systems.find(system => system.name === newValue);
        configCopy.region = systemInfo.regionName;

        setConfig(configCopy);
    };

    const saveConfig = () => {
        setModified(false);
        if (!isNewLink) {
            ZkiliRedirectsApi.update(id, config)
                .then(result => {
                    if (result.linkId) {
                        openSnackbar('Your link has been updated!');
                        setLinkId(result.linkId);
                    } else if (result.error) {
                        openSnackbar(result.error);
                    }
                })
                .catch(error => {
                    console.error(error);
                    if (error?.response?.data?.error) {
                        openSnackbar(error?.response?.data?.error);
                    } else {
                        openSnackbar('Failed to update your link, something went wrong.');
                    }
                });
        } else {
            ZkiliRedirectsApi.create(config)
                .then(result => {
                    if (result.linkId) {
                        openSnackbar('Your link has been saved!');
                        setLinkId(result.linkId);
                        history.push(`/zkili-links/custom/${result.id}`);
                    }
                })
                .catch(error => {
                    console.error(error);
                    if (error?.response?.data?.error) {
                        openSnackbar(error?.response?.data?.error);
                    } else {
                        openSnackbar('Failed to save your link, something went wrong.');
                    }
                });
        }
    };

    const showDeleteConfirm = () => setShowDelete(true);
    const hideDeleteConfirm = () => setShowDelete(false);
    const deleteLink = () => {
        hideDeleteConfirm();
        ZkiliRedirectsApi.deleteLink(id)
            .then(result => {
                console.log(result);

                if (result?.success) {
                    openSnackbar('Your link has been deleted!');
                    history.push('/zkili-links/custom');
                } else {
                    openSnackbar('Failed to delete your link!');
                }
            })
            .catch(error => {
                console.error(error);
                if (error?.response?.data?.error) {
                    openSnackbar(error?.response?.data?.error);
                } else {
                    openSnackbar('Failed to delete your link, something went wrong.');
                }
            });
    };
    const copyLink = link => event => {
        copyToClipboard(link);
        openSnackbar('Copied link to your clipboard.');
    };

    useEffect(() => {
        dispatch(fetchShips());
        dispatch(fetchSystems());
        if (!isNewLink) {
            ZkiliRedirectsApi.get(id)
                .then(result => {
                    setConfig(result.config);
                    setLinkId(result.linkId);
                })
                .catch(error => {
                    console.log(error.response);
                    if (error?.response?.data?.error) {
                        openSnackbar(error?.response?.data?.error);
                    } else {
                        openSnackbar('Failed to fetch your link config, something went wrong.');
                    }
                    history.push('/zkili-links/custom');
                });
        } else {
            setModified(true);
        }
    }, [id]);
    useEffect(() => {
        const newShipOptions = ships.map(ship => ship.typeName).sort();
        setShipOptions(newShipOptions);
    }, [ships]);
    useEffect(() => {
        const newSystemOptions = systems
            .filter(system => system.name !== 0)
            .map(system => system.name)
            .sort();
        setSystemOptions(newSystemOptions);
    }, [systems]);

    if (!user) {
        return (
            <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12} md={6} className={classes.loginFirst}>
                    <Typography variant="h5">Login using your Eve account before continuing</Typography>
                    <a href={`${process.env.REACT_APP_API_BASE}/api/auth/eve-login`}>
                        <img className={classes.loginImageLink} src={ssologinImage} alt="Eve online sso button" />
                    </a>
                    <Typography>Links are connected to your Eve characters to edit them later</Typography>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container spacing={1} justifyContent="center">
            <SEO
                title="Custom Zkili Link"
                path="/zkili-links/custom"
                description="Create custom redirect links for Zkili for El'miner"
            />

            <Dialog open={showDelete} onClose={hideDeleteConfirm}>
                <DialogTitle>Are you sure you wish to delete this link?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deleting this link will break it for anyone viewing, linking or clicking it! There is no way to
                        undo this!
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={hideDeleteConfirm} autoFocus color="primary">
                        Cancel
                    </Button>
                    <Button onClick={deleteLink} color="warning">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {!shownInfo && (
                <>
                    <Grid item xs={12} md={6}>
                        <Alert color="info" onClose={() => setShownInfo(true)}>
                            Currently only 1 custom link can be made per EVE character. There will be support for
                            multiple later on once everything has been tested a bit more thoroughly.
                        </Alert>
                    </Grid>
                    <Grid item xs={12} />
                </>
            )}

            {!shownWarning && (
                <>
                    <Grid item xs={12} md={6}>
                        <Alert color="warning" onClose={() => setShownWarning(true)}>
                            Discord will cache how the embeded link looks for ~30 minutes! This means it might take a
                            bit before Discord will show changes for the same link. Switching between the two links will
                            help avoid this issue aswell.
                        </Alert>
                    </Grid>
                    <Grid item xs={12} />
                </>
            )}

            <Grid item xs={12} md={6} className={classes.previewContainer}>
                <Paper className={classes.previewPaper}>
                    <div style={{ flexGrow: 1, marginRight: 8 }}>
                        <Typography variant="caption">zkillboard.com</Typography>
                        <br />
                        <Link className={classes.previewLink} underline="none">
                            {config.ship} | {config.name} | Killmail
                        </Link>
                        <Typography className={classes.previewText}>
                            {config.name} ({config.corporation}) lost their {config.ship} in {config.system} (
                            {config.region}). Final Blow by {config.aggressorName} ({config.aggressorCorporation})
                            flying in a {config.aggressorShip}. Total Value:{' '}
                            {parseFloat(String(config.value || 0)).toLocaleString('us', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}{' '}
                            ISK
                        </Typography>
                    </div>
                    <div className={classes.previewImgLink}>
                        {config.shipId && (
                            <img
                                alt="Preview zkili link render"
                                src={`https://images.evetech.net/types/${config.shipId}/icon`}
                                className={classes.previewImg}
                            />
                        )}
                    </div>
                </Paper>
            </Grid>

            {linkId && (
                <>
                    <Grid item xs={12} />

                    <Grid item xs={12} md={6}>
                        <Paper className={classes.copyPaper}>
                            <FormControl fullWidth variant="standard">
                                <Input
                                    value={`https://zkiliboard.com/kill/${linkId}/`}
                                    inputProps={{ readOnly: true }}
                                    onClick={copyLink(`https://zkiliboard.com/kill/${linkId}/`)}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <ContentCopy />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} />

                    <Grid item xs={12} md={6}>
                        <Paper className={classes.copyPaper}>
                            <FormControl fullWidth variant="standard">
                                <Input
                                    value={`https://zkilllboard.com/kill/${linkId}/`}
                                    inputProps={{ readOnly: true }}
                                    onClick={copyLink(`https://zkilllboard.com/kill/${linkId}/`)}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <ContentCopy />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </Paper>
                    </Grid>
                </>
            )}

            <Grid item xs={12} />

            <Grid item xs={12} md={6}>
                <Paper className={classes.formPaper}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            {isNewLink ? (
                                <Typography variant="h6">Create a new link</Typography>
                            ) : (
                                <Typography variant="h6">Update your link</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                value={config.redirect}
                                className={classes.formField}
                                error={redirectError}
                                placeholder="https://zkillboard.com/kill/29682967/"
                                onChange={onChange('redirect')}
                                label="Redirect link"
                                helperText="Only zkillboard.com links are valid, will default to El'miner"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <TextField
                                value={config.name}
                                className={classes.formField}
                                error={nameError}
                                onChange={onChange('name')}
                                label="Name"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <TextField
                                value={config.corporation}
                                className={classes.formField}
                                error={corporationError}
                                onChange={onChange('corporation')}
                                label="Corporation"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <Autocomplete
                                disablePortal
                                options={shipOptions}
                                value={config.ship}
                                onChange={onShipChange}
                                className={classes.formField}
                                renderInput={params => <TextField {...params} label="Destroyed ship" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <TextField
                                value={config.value}
                                className={classes.formField}
                                error={valueError}
                                onChange={onChange('value')}
                                label="Loss value"
                                InputProps={{
                                    type: 'number',
                                    inputProps: {
                                        min: 0,
                                        max: 10000000000000
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} />

                        <Grid item xs={12} sm={6} lg={4}>
                            <TextField
                                value={config.aggressorName}
                                className={classes.formField}
                                error={aggressorNameError}
                                onChange={onChange('aggressorName')}
                                label="Aggressor Name"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <TextField
                                value={config.aggressorCorporation}
                                className={classes.formField}
                                error={aggressorCorporationError}
                                onChange={onChange('aggressorCorporation')}
                                label="Aggressor Corporation"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <Autocomplete
                                disablePortal
                                options={shipOptions}
                                value={config.aggressorShip}
                                onChange={onAggressorShipChange}
                                className={classes.formField}
                                renderInput={params => <TextField {...params} label="Aggressor ship" />}
                            />
                        </Grid>

                        <Grid item xs={12} />

                        <Grid item xs={12} sm={6} lg={4}>
                            <Autocomplete
                                disablePortal
                                options={systemOptions}
                                value={config.system}
                                onChange={onSystemChange}
                                className={classes.formField}
                                filterOptions={systemFilterOptions}
                                renderInput={params => <TextField {...params} label="System" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4}>
                            <TextField
                                value={config.region}
                                className={classes.formField}
                                label="Region"
                                InputProps={{
                                    readOnly: true
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} />

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Box sx={{ marginRight: 'auto' }}>
                                    {!isNewLink && (
                                        <Button variant="outlined" color="warning" onClick={showDeleteConfirm}>
                                            Delete link
                                        </Button>
                                    )}
                                </Box>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    component={RouterLink}
                                    to="/zkili-links/custom"
                                    className={classes.cancelButton}
                                >
                                    Back
                                </Button>
                                <Button variant="outlined" disabled={!modified || !formIsValid} onClick={saveConfig}>
                                    {isNewLink ? 'Create' : 'Save'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default ZkillCustom;
